<template>
  <div class="ls-container">
    <div class="ls-gwselector">
      <label>Gewässer für die Längsschnittdarstellung wählen:
        <select v-model="selectedGewaesser" @change="onselectNewGewaesser" id="selectGewaesser">
          <option value="" disabled selected>Gewässer auswählen</option>
          <option v-for="aGewaesser in gewaessernetz.gewaesserAuswahl.value" :value="aGewaesser.nr">
            {{ aGewaesser.nr }}: <template v-for="name in aGewaesser?.name"> {{ name.concat(' ') }}</template>
          </option>
        </select>
      </label>
      <button v-if="zoom" @click="resetAxis">Achsen zurücksetzen</button>
    </div>
    <div class="ls-chart" ref="chartContainer">
      <canvas ref="chartCanvas" ></canvas>
    </div>
  </div>
</template>

<script lang="ts">
import { Chart } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { CategoryScale, ScatterController, LinearScale, PointElement, LineElement, Legend, Tooltip } from 'chart.js'; 
Chart.register(CategoryScale, ScatterController, LinearScale, PointElement, LineElement, Legend, Tooltip, zoomPlugin )
import { onMounted, ref, computed, watch } from 'vue';
import setupChartData from './setupChartData';
import type { Ref } from 'vue';
import type { ChartDataset } from 'chart.js';
import gewaessernetz from "./Gewaessernetz.ts";
import NasimMapviewtour from "../NasimMapviewtour";
import { getRelativePosition } from 'chart.js/helpers';
import predefinedColors from './predefinedColors';
import debounce from "vueforms/src/utils/debounce";
import {visibleHeight,visibleWidth} from "vueforms/src/utils/visibleSize.ts";

export default {
  props: {
    selectedData: {
      type: Object,
      required: true
    }
  },
  emits: ["gewaesserChanged", "lineHover", "noLS", "lineClick", "abfluss"],

  setup(props, {emit}) {
    let chartData = null;
    let chart : Chart;
    const chartCanvas :Ref<HTMLElement|undefined> = ref();
    const chartContainer :Ref<HTMLElement|undefined> = ref();
    const laengstesGewaesser : Ref<string | undefined> = ref(); //Um es in der Einführungstour auszuwählen
    const selectedGewaesser : Ref<string | undefined> = ref("");


    watch(() => props.selectedData.selectedGewaesser.value, (newVal) => { 
      selectedGewaesser.value = newVal ? newVal:"";
      loadAllLSForNewGewaesser();
    })

    watch(() => props.selectedData.getMovingKm(), (newVal) => {
      if (!newVal) {
        chart.corsair = {
          x: 0,
          y: 0,
          draw: false
        }
        chart.draw();
        return;
      }
      let min = chart.scales.x.min;
      let max = chart.scales.x.max;
      let chartWidth = chart.chartArea.right - chart.chartArea.left;
      let x = chart.chartArea.left + ((max - newVal)/(max - min)) * chartWidth;
      chart.corsair = {
        x: x,
        y: 0,
        draw: true
      }
      chart.draw();
    })

    const drawLine = (km) => {
      //chart.draw();
      if (!km) {
        return;
      }
      let min = chart.scales.x.min;
      let max = chart.scales.x.max;
      let chartWidth = chart.chartArea.right - chart.chartArea.left;
      let x = chart.chartArea.left + ((max - km)/(max - min)) * chartWidth;
      let context = chartCanvas.value.getContext("2d");
      context.beginPath();
      context.moveTo(x, chart.chartArea.bottom);
      context.lineTo(x, chart.chartArea.top);
      context.stroke();
      context.save();
    }

    watch(() => props.selectedData.getFixedKm()?.km, (km) => {
      drawLine(km);
      //Abfluss ermitteln:
      let abfluss = {};
      let series = chart.data.datasets
                     .filter((dataset, index) => !chart.legend.legendItems[index].hidden)
                     .map((dataset, index) => {return { dataset, index };});
      series.map(obj => {
        if (!abfluss[obj.dataset.label]) {
          abfluss[obj.dataset.label] = getAbfluss(obj, km, chart.scales.x.getPixelForValue(km));
        }
      })
      emit("abfluss", abfluss);
    });
  
    watch(laengstesGewaesser,() => {NasimMapviewtour.setLaengstesGewaesser(laengstesGewaesser.value)});

    const adjustSize= debounce(function (){
      const h = visibleHeight(chartContainer.value);
      const w = visibleWidth(chartContainer.value);
      chartCanvas.value.style.height = h + "px";
      chartCanvas.value.style.width = w + "px";
      chart.resize(w,h);
    }, 200);

    onMounted(() => {
      const onZoomComplete = () => {zoom.value = true;}
      const onHover = (e, activeElements) => {
        const canvasPosition = getRelativePosition(e, chart);
        const dataX = chart.scales.x.getValueForPixel(canvasPosition.x); 
        emit("lineHover", dataX);
      }
      const onClick = (e, activeElements) => {
        const canvasPosition = getRelativePosition(e, chart);
        const dataX = chart.scales.x.getValueForPixel(canvasPosition.x); 
        let abfluss = {};
        //Fall, dass direkt auf einen Punkt geklickt wurde:
        let qValuesDirectClick = chart.getElementsAtEventForMode(e, 'nearest', { intersect: true }, true);
        if (qValuesDirectClick.length > 0) { 
          qValuesDirectClick.map(val => {
            //checken, ob es mehrere Punkte auf demselben km im Dataset gibt
            let test = findPointWithSameXCoord({point: chart.data.datasets[val.datasetIndex].data[val.index], index: val.index }, chart.data.datasets[val.datasetIndex]);
            if (test) {
              abfluss[chart.data.datasets[val.datasetIndex].label] = [chart.data.datasets[val.datasetIndex].data[val.index].y, test.y]
            } else {
              abfluss[chart.data.datasets[val.datasetIndex].label] = [chart.data.datasets[val.datasetIndex].data[val.index].y];
            }
          });
        }
        let series = chart.data.datasets.filter((dataset, index) => !chart.legend.legendItems[index].hidden).map((dataset, index) => {
          return { dataset: dataset, index: index };
        });
        //Fall, dass zwischen Punkten geklickt wurde: 
        series.map(obj => {
          if (!abfluss[obj.dataset.label]) {
            abfluss[obj.dataset.label] = getAbfluss(obj, dataX, canvasPosition.x);
          }
        })
        drawLine(dataX);
        emit("lineClick", dataX, abfluss);
      }
      const afterDatasetsDraw = (chart, _, opts) => {
        const {ctx,chartArea: {top,bottom,left,right}} = chart;
        const {x,y,draw } = chart.corsair;

        ctx.lineWidth = opts.width || 0;
        ctx.setLineDash(opts.dash || []);
        ctx.strokeStyle = opts.color || 'black'
        ctx.save();
        if (props.selectedData.getFixedKm()?.km) {
          drawLine(props.selectedData.getFixedKm()?.km);
        }

        if (!draw) {
          return;
        }

        ctx.save();
        ctx.beginPath();
        ctx.moveTo(x, bottom);
        ctx.lineTo(x, top);
        ctx.stroke();
        //ctx.restore();
      }       
      chartData = setupChartData(chartCanvas.value, onZoomComplete, onHover, onClick, afterDatasetsDraw);
      chart = new Chart(chartCanvas.value, chartData);
      window.addEventListener("resize", adjustSize);
    });

    /**
     * @description Hilfsfunktion, die ein ein neus Dataset (MQ, HQxy, Max. SE-Abfluss) in das Chart lädt.
     * @param type String. 'MQ','HQxy' oder 'Max. SE-Abfluss'
     * @param gewaesserNr String. Die Gewässernummer des Gewässers, für das die Daten in das Chart geladen werden.
     */
    function pushDataSet(type: string, dataSet:Array<any>, isHidden: boolean = false) {

      let newDataset = {
        label: type,
        data: dataSet,
        showLine: true,
        borderWidth: 2,
        borderColor: predefinedColors[chartData.data.datasets.length % predefinedColors.length],
        backgroundColor: 'rgba(0, 0, 0, 0)',
        lineTension: 0,
        animation: {
          duration: 200
        },
        hidden: isHidden
      }
      chartData.data.datasets.push(newDataset);
      chart.update();
    }

    /**
     * @description Die Funktion onselectNewGewaesser ist ein Listener für das Dropwdown-Menü
     * Sie emittet "gewaesserChanged", woraufhin das property selectedGewaesser für die NasimMap verändert wird.
     */
    function onselectNewGewaesser() {
      emit('gewaesserChanged', selectedGewaesser.value);
      resetAxis();
    }

    /**
     * @description Alle angecheckten Datasets (MQ, HQ ...) werden für das selectedGewaesser in das Chart geladen.
     * Diese Funktion wird von einem watcher des selectedGewaesser Props aufgerufen.
     */
    function loadAllLSForNewGewaesser() {

      // Welche Datensaetze hat Nutzer unsichtbar geschaltet? Die merken wir uns, weil wir sie auch kuenftig unsichtbar lassen wollen
      const hiddenDatasetsLabels = chart.data.datasets.
                                     filter( (dataset, index) => !chart.isDatasetVisible(index)).
                                     map((dataset, index) => dataset.label);
      chart.data.datasets = [];
      if (!selectedGewaesser.value) {
        chart.update();
        return;
      }
      let gewaesser = gewaessernetz.gewaesserMap.value[selectedGewaesser.value];
      if (!gewaesser) {
        chart.update();
        console.error("Gewässernummer " + selectedGewaesser.value + " ist nicht in gewaesserMap vorhanden.");
        return;
      }
      gewaessernetz.getGewaesserSeries(gewaesser).then((allSeries) => {
        let atLeastOneDatasetSufficient = false;
        for (let i=0; i < allSeries.length; i++) {
          if (allSeries[i]) {
            const label = gewaesser.laengsschnitte.value[i].typ;
            pushDataSet(label, allSeries[i],hiddenDatasetsLabels.includes(label) );
            atLeastOneDatasetSufficient = true;
          }
        }
        if (!atLeastOneDatasetSufficient) {
          emit('noLS');
          chart.update();
        }
        resetAxis();
      });

    }

    let resetAxis = () => {
      chart.resetZoom();
      zoom.value = false;
    }

    let zoom = ref(false);

    /**
     * Hilfsfunktion für onClick Event im Chart: Findet für die Abflusswertabfrage die beiden Punkte rechts und links vom Klick
     * kompliziert weil bei Punkten mit selben x-wert die Chart API den falschen als nächsten Punkt findet
     * @param obj {dataset: Das Dataset, auf dem gesucht wird, index: Index des Datasets}
     * @param dataX Gewässer Km an Klick
     * @param canvasPositionX x-Position im Canas des Klicks
     * @return entweder undefined oder ein Array mit Abflusswerten an diesem Klick
     */
    function getAbfluss(obj: object, dataX: number, canvasPositionX: number) {
      
      let left = findLeftNeighbour(obj.dataset,  dataX);
      let right = findRightNeighbour(obj.dataset, dataX);

      //Überprüfen, wie nah an Punkt geklickt wurde
      let meta = chart.getDatasetMeta(obj.index);


      //Wenn sehr nah an Punkt geklickt wurde, prüfen, ob es weiteren Punkt an derselben x-Koordinate gibt, und beide Abflusswerte zeigen
      if (left) {
        let leftCanvasX = meta.data[left.index].x;
        if (Math.abs(canvasPositionX - leftCanvasX) <= 2) {
          let pointWithSameXCoord = findPointWithSameXCoord(left, obj.dataset);
          if (pointWithSameXCoord) {
            return [left.point.y, pointWithSameXCoord.y];
          }
        }

      }
      if (right) {
        let rightCanvasX = meta.data[right.index].x;
        if (Math.abs(canvasPositionX - rightCanvasX) <= 2) {
          let pointWithSameXCoord = findPointWithSameXCoord(right, obj.dataset);
          if (pointWithSameXCoord) {
            return [right.point.y, pointWithSameXCoord.y];
          }
        }
      }
      //Ansonten interpolieren
      if (left && right) {
        let a = (dataX - left.point.x) / (right.point.x - left.point.x);
        return [linearInterpolation(left.point.y, right.point.y, a)];
      } else {
        return undefined;
      }

    }

    /**
     * Lineare Interpolation
     * @param left Abflusswert links vom Klick
     * @param right Abflusswert rechts vom Klick
     * @param amount Wo liegt der Klick zwischen den beiden Puntken (prozentsatz)
     **/
    function linearInterpolation(left: number, right: number, amount: number) {
      return left * (1 - amount) + right * amount
    }

    /**
     * Hilfsfunktion für Abflussbafrage, wenn sehr nah an Punkt geklickt wurde,
     * um zu prüfen, ob es einen Punkt mit derselben Gewässerkm gibt
     * @param point {point: point, index: number}
     * @dataset das entsprechende Dataset
     * @return einen Punkt {x: number, y: number} mit derselben x-Koordinate wie point oder undefined, falls es den nicht gibt
     */
    function findPointWithSameXCoord(point, dataset) {
      if (point.index > 0) {
        let nextElemLeft = dataset.data[point.index - 1];
        if (nextElemLeft.x == point.point.x) {
          return nextElemLeft;
        }
      }
      if (point.index < (dataset.data.length - 1)) {
        let nextElemRight = dataset.data[point.index + 1];
        if (nextElemRight.x == point.point.x) {
          return nextElemRight;
        }
      }
      return false;
    }

    /**
     * Funktion, welche den Nachbarpunkt links des Klicks findet. 
     * @param dataset Das Dataset, (MQ, HQ1 ...), auf dem gesucht wird
     * @param dataX Gewässer Km an Klick
     */
    function findLeftNeighbour(dataset: ChartDataset, dataX: number) {
      let pointIndex = dataset.data.length - 1;
      let point = dataset.data[pointIndex];
      while(dataX > point.x && pointIndex > 0) {
        pointIndex--;
        point = dataset.data[pointIndex]

      }
      if (dataX > point.x) {
        //Es wurde links neben dem ersten Punkt geklickt und es gibt keinen linken Nachbarn
        return undefined;
      } else {
        return { point: point, index: pointIndex };
      }
    }

    /**
     * Funktion, welche den Nachbarpunkte rechts des Klicks findet. 
     * @param dataset Das Dataset, (MQ, HQ1 ...), auf dem gesucht wird
     * @param dataX Gewässer Km an Klick
     */
    function findRightNeighbour(dataset: ChartDataset, dataX: number) {
      let pointIndex = 0;
      let point = dataset.data[pointIndex];
      while(dataX < point.x && pointIndex < (dataset.data.length - 1)) {
        pointIndex++;
        point = dataset.data[pointIndex];
      }
      if (dataX < point.x) {
        //Es wurde rechts neben dem letztem Punkt geklickt und es gibt keinen rechten Nachbarn
        return undefined;
      } else {
        return { point: point, index: pointIndex };
      }
    }

    return {
      selectedGewaesser,
      gewaessernetz,
      onselectNewGewaesser,
      resetAxis,
      zoom,
      chartCanvas,
      chartContainer,
      adjustSize
    }
  }
}
</script>
 
<style>
.ls-container {
  display: flex;
  flex-direction: column; /* Elemente untereinander anordnen */
  height: 100%; /* Gesamte Höhe des Viewports ausnutzen */
  width: 100%; /* Volle Breite */
  box-sizing: border-box; /* Padding und Border in Breite/Höhe einbeziehen */
  overflow: hidden;
}

.ls-gwselector {
  width: 100%; /* Volle Breite */
  text-align: center;
  box-sizing: border-box; /* Padding und Border in Breite/Höhe einbeziehen */
}

.ls-chart {
  flex: 1; /* Nimmt den restlichen verfügbaren Platz ein */
  width: 100%; /* Volle Breite */
  box-sizing: border-box; /* Padding und Border in Breite/Höhe einbeziehen */
}
</style>